// src/components/profile/ProfilePage.js
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db, storage } from '../../services/firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import ImageCropper from '../common/ImageCropper';
import './ProfilePage.css';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const PersonPlaceholder = () => (
  <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" width="60" height="60">
    <circle cx="50" cy="32" r="20" fill="rgba(255,255,255,0.6)" />
    <ellipse cx="50" cy="85" rx="32" ry="22" fill="rgba(255,255,255,0.6)" />
  </svg>
);

const ProfilePage = () => {
  const { currentUser, userData, refreshUserData, logout } = useAuth();
  const [language, setLanguage] = useState(
    () => localStorage.getItem('spocademy_language') || 'mr'
  );
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const fileInputRef = useRef();
  const cameraInputRef = useRef();

  const t = language === 'mr' ? {
    myProfile: 'माझे प्रोफाइल',
    uploadPrompt: 'तुमचा फोटो upload करा (कमाल 10MB)',
    fromGallery: 'गॅलरी',
    fromCamera: 'कॅमेरा',
    removePhoto: 'फोटो delete',
    sizeError: 'फोटो 10MB पेक्षा लहान असावा. कृपया छोटा फोटो निवडा.',
    uploadError: 'फोटो upload अयशस्वी. पुन्हा प्रयत्न करा.',
    uploading: 'upload होत आहे...',
    fitnessPoints: 'फिटनेस गुण',
    lekhiPoints: 'लेखी गुण',
    testPoints: 'परीक्षा गुण',
    totalPoints: 'एकूण गुण',
    member: 'सदस्य तारखेपासून',
    streak: 'स्ट्रीक',
    points: 'एकूण गुण',
    notifications: 'सूचना चालू करा',
    notifDenied: 'ब्राउझरने परवानगी नाकारली',
    language: 'EN',
    logout: 'लॉग आउट',
    contact: 'मदत हवी आहे?'
  } : {
    myProfile: 'My Profile',
    uploadPrompt: 'Upload your photo (max 10MB)',
    fromGallery: 'Gallery',
    fromCamera: 'Camera',
    removePhoto: 'Remove Photo',
    sizeError: 'Photo must be under 10MB. Please choose a smaller photo.',
    uploadError: 'Upload failed. Please try again.',
    uploading: 'Uploading...',
    fitnessPoints: 'Fitness Points',
    lekhiPoints: 'Lekhi Points',
    testPoints: 'Test Points',
    totalPoints: 'Total Points',
    member: 'Member since',
    streak: 'Streak',
    points: 'Total Points',
    notifications: 'Enable Notifications',
    notifDenied: 'Browser denied permission',
    language: 'मराठी',
    logout: 'Logout',
    contact: 'Need help?'
  };

  useEffect(() => {
    const browserGranted = typeof Notification !== 'undefined' && Notification.permission === 'granted';
    if (browserGranted) {
      setNotificationEnabled(true);
    } else if (userData?.notificationEnabled !== undefined) {
      setNotificationEnabled(userData.notificationEnabled);
    }
  }, [userData]);

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
  };

  const handleNotificationToggle = async () => {
    if (notificationLoading) return;
    setNotificationLoading(true);
    try {
      if (!notificationEnabled) {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          alert(t.notifDenied);
          setNotificationLoading(false);
          return;
        }
      }
      const newVal = !notificationEnabled;
      setNotificationEnabled(newVal);
      await updateDoc(doc(db, 'users', currentUser.uid), { notificationEnabled: newVal });
    } catch (e) {}
    setNotificationLoading(false);
  };

  // Step 1: user picks a file → validate size → open the cropper
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setPhotoError('');
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setPhotoError(t.sizeError);
      return;
    }
    setCropImageSrc(URL.createObjectURL(file));
  };

  // Step 2: cropper returns a cropped square file → upload it
  const handleCropComplete = async (croppedFile) => {
    const srcToRevoke = cropImageSrc;
    setCropImageSrc(null);
    if (srcToRevoke) URL.revokeObjectURL(srcToRevoke);
    setPhotoUploading(true);
    setPhotoError('');
    try {
      const storageRef = ref(storage, `profile_photos/${currentUser.uid}`);
      await uploadBytes(storageRef, croppedFile);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'users', currentUser.uid), { photoURL: url });
      if (refreshUserData) await refreshUserData();
    } catch (e) {
      setPhotoError(t.uploadError);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleCropCancel = () => {
    const srcToRevoke = cropImageSrc;
    setCropImageSrc(null);
    if (srcToRevoke) URL.revokeObjectURL(srcToRevoke);
  };

  const handleRemovePhoto = async () => {
    if (!window.confirm(language === 'mr' ? 'फोटो काढायचा आहे का?' : 'Remove profile photo?')) return;
    setPhotoUploading(true);
    try {
      const storageRef = ref(storage, `profile_photos/${currentUser.uid}`);
      await deleteObject(storageRef).catch(() => {});
      await updateDoc(doc(db, 'users', currentUser.uid), { photoURL: '' });
      if (refreshUserData) await refreshUserData();
    } catch (e) {
      setPhotoError(t.uploadError);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleWhatsApp = () => {
    const msg = language === 'mr'
      ? 'नमस्कार, मला मदत हवी आहे.'
      : 'Hi, I need help with Spocademy.';
    window.open(`https://wa.me/919359246193?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const getCategoryLabel = (fitnessLevel) => {
    const map = {
      beginnerBoys: 'Cheetah · Boys',
      beginnerGirls: 'Cheetah · Girls',
      advancedBoys: 'Wagh · Boys',
      advancedGirls: 'Wagh · Girls',
      specialBoys: 'Sinh · Boys',
      specialGirls: 'Sinh · Girls'
    };
    return map[fitnessLevel] || fitnessLevel;
  };

  const getBatchStartDate = () => {
    const dateField = userData?.fitnessStartDate;
    if (!dateField) return '';
    const date = dateField.toDate ? dateField.toDate() : new Date(dateField);
    return date.toLocaleDateString(language === 'mr' ? 'mr-IN' : 'en-IN', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const fitnessPoints = userData?.fitnessPoints || 0;
  const testPoints = (userData?.lekhiTestsTaken || 0) * 2;
  const lekhiDailyPoints = Math.max(0, (userData?.lekhiPoints || 0) - testPoints);
  const totalPoints = fitnessPoints + (userData?.lekhiPoints || 0);
  const firstName = userData?.firstName || '';
  const lastName = userData?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return (
    <div className="profile-container">

      <div className="profile-header-bar">
        <div className="profile-title">{t.myProfile}</div>
        <button className="profile-lang-btn" onClick={handleLanguageToggle}>
          {t.language}
        </button>
      </div>

      <div className="profile-scroll">

        <div className="profile-identity-section">
          <div className="profile-full-name">{fullName}</div>
          <div className="profile-village">{userData?.village}</div>
          <div className="profile-meta-row">
            {userData?.fitnessLevel && (
              <span className="profile-category">{getCategoryLabel(userData.fitnessLevel)}</span>
            )}
            {userData?.fitnessStartDate && (
              <span className="profile-member-since">{t.member} {getBatchStartDate()}</span>
            )}
          </div>
        </div>

        <div className="profile-photo-section">
          <div className="profile-photo-wrap">
            {userData?.photoURL ? (
              <img src={userData.photoURL} alt="Profile" className="profile-photo" />
            ) : (
              <div className="profile-photo-placeholder">
                <PersonPlaceholder />
              </div>
            )}
          </div>
          <div className="profile-upload-area">
            <p className="profile-upload-prompt">{t.uploadPrompt}</p>
            <div className="profile-photo-btns">
              <button className="profile-photo-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={photoUploading}>
                {t.fromGallery}
              </button>
              <button className="profile-photo-btn"
                onClick={() => cameraInputRef.current?.click()}
                disabled={photoUploading}>
                {t.fromCamera}
              </button>
              {userData?.photoURL && (
                <button className="profile-photo-btn profile-photo-btn-danger"
                  onClick={handleRemovePhoto}
                  disabled={photoUploading}>
                  {t.removePhoto}
                </button>
              )}
            </div>
            {photoUploading && <p className="profile-uploading">{t.uploading}</p>}
            {photoError && <p className="profile-photo-error">{photoError}</p>}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileSelect} />
          <input ref={cameraInputRef} type="file" accept="image/*" capture="user"
            style={{ display: 'none' }}
            onChange={handleFileSelect} />
        </div>

        <div className="profile-stats-row">
          <div className="profile-stat-card">
            <div className="profile-stat-number">{fitnessPoints}</div>
            <div className="profile-stat-label">{t.fitnessPoints}</div>
          </div>
          <div className="profile-stat-card">
            <div className="profile-stat-number">{lekhiDailyPoints}</div>
            <div className="profile-stat-label">{t.lekhiPoints}</div>
          </div>
        </div>

        <div className="profile-stats-row">
          <div className="profile-stat-card">
            <div className="profile-stat-number">{testPoints}</div>
            <div className="profile-stat-label">{t.testPoints}</div>
          </div>
          <div className="profile-stat-card">
            <div className="profile-stat-number">{totalPoints}</div>
            <div className="profile-stat-label">{t.totalPoints}</div>
          </div>
        </div>

        <div className="profile-stats-row">
          <div className="profile-stat-card">
            <div className="profile-stat-number">{userData?.streakCount || 0} 🔥</div>
            <div className="profile-stat-label">{t.streak}</div>
          </div>
        </div>

        <div className="profile-notification-row">
          <span className="profile-notification-label">{t.notifications}</span>
          <button
            className={`profile-toggle ${notificationEnabled ? 'on' : 'off'}`}
            onClick={handleNotificationToggle}
            disabled={notificationLoading}>
            <div className="profile-toggle-thumb" />
          </button>
        </div>

        <button className="profile-logout-btn" onClick={logout}>
          {t.logout}
        </button>

      </div>

      <button className="profile-whatsapp-float" onClick={handleWhatsApp}>
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="white" width="28" height="28">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </button>

      {cropImageSrc && (
        <ImageCropper
          imageSrc={cropImageSrc}
          language={language}
          onCancel={handleCropCancel}
          onCropComplete={handleCropComplete}
        />
      )}

    </div>
  );
};

export default ProfilePage;